import { Vertex2 } from '@rta/rta-blueprint-library';

import { getVectorAngle } from './get-vector-angle';
import { type Vector } from '@r-core/types';

/**
 * Normalizes horizontal/vertical vectors to have consistent direction for overlap detection.
 * - Horizontal vectors (angle ~90 or ~-90): normalized to go left-to-right (from.x < to.x)
 * - Vertical vectors (angle ~0 or ~180): normalized to go bottom-to-top (from.y < to.y)
 * - Diagonal vectors: returned unchanged (overlap detection doesn't apply)
 */
export const fixVectorDirection = (vector: Vector<Vertex2>): Vector<Vertex2> => {
	const angle = vector.angle;
	const tolerance = 1; // Allow small angle tolerance for near-horizontal/vertical

	// Check if it's a horizontal vector (angle near 90 or -90)
	const isHorizontal = Math.abs(Math.abs(angle) - 90) < tolerance;
	// Check if it's a vertical vector (angle near 0 or 180 or -180)
	const isVertical = Math.abs(angle) < tolerance || Math.abs(Math.abs(angle) - 180) < tolerance;

	// For diagonal vectors, return unchanged - overlap detection doesn't apply
	if (!isHorizontal && !isVertical) {
		return vector;
	}

	// Cache the original coordinates
	const fromX = vector.from.x;
	const fromY = vector.from.y;
	const toX = vector.to.x;
	const toY = vector.to.y;

	let newFromX = fromX;
	let newFromY = fromY;
	let newToX = toX;
	let newToY = toY;

	// For horizontal vectors pointing left (angle -90), swap to point right
	if (isHorizontal && angle < 0 && fromX > toX) {
		newFromX = toX;
		newToX = fromX;
		newFromY = toY;
		newToY = fromY;
	}

	// For vertical vectors pointing down (from.y > to.y), swap to point up
	if (isVertical && fromY > toY) {
		newFromX = toX;
		newToX = fromX;
		newFromY = toY;
		newToY = fromY;
	}

	// Use plain objects to avoid Vertex2 getter issues
	const newVector: Vector<Vertex2> = {
		...vector,
		from: { x: newFromX, y: newFromY } as Vertex2,
		to: { x: newToX, y: newToY } as Vertex2
	};

	newVector.angle = getVectorAngle({ from: newVector.from, to: newVector.to });

	return newVector;
};

